import { useHover } from "@redotech/react-util/hover";
import DotsReorderIcon from "@redotech/redo-icons/dots-reorder.svg";
import FolderOpenIcon from "@redotech/redo-icons/folder-open.svg";
import FolderIcon from "@redotech/redo-icons/folder.svg";
import TriangleDown from "@redotech/redo-icons/triangle-down_filled.svg";
import classNames from "classnames";
import { memo, useEffect, useState } from "react";
import { matchPath, useLocation } from "react-router-dom";
import { Flex } from "../../../../flex";
import { Text } from "../../../../text";
import { OverflowTooltip } from "../../../../tooltip/overflow-tooltip";
import { RedoBadge } from "../../../badge/redo-badge";
import { RedoIcon } from "../../../icon/redo-icon";
import * as navigationCss from "../../redo-navigation.module.css";
import { formatNotificationCount } from "../../secondary/format-notification-count";
import { secondaryNavTabTestId } from "../secondary-nav-tab-test-id";
import type { TreeIconComponent } from "../types";
import * as folderItemCss from "./tree-item.module.css";

export interface RedoFolderItemProps {
  name: string;
  "data-dd-action-name"?: string;
  friendlyName: string;
  dragHandleProps: object;
  onToggleExpanded: () => void;
  onRequestEdit?: () => void;
  childrenLinks: string[];
  /**
   * Total direct children of this folder, used to size the connector pipe
   * when expanded. Falls back to `childrenLinks.length` when not provided
   * (preserves existing OMS behavior where every child is a linkable view).
   */
  childCount?: number;
  expanded: boolean;
  notificationCount?: number;
  clone?: boolean;
  /** Escape hatch for state-driven selection. When true, the folder renders with the active highlight. */
  selected?: boolean;
  /**
   * When provided, body-click selects the folder as well as expanding it, so
   * the whole row is the affordance and the chevron is never required.
   */
  onClick?: () => void;
  /**
   * Fires on every body click in addition to the primary expand/select behavior.
   * Use for analytics or other side effects that should not replace the action.
   */
  onItemClick?: () => void;
  /** Optional custom icon for the expanded state. Defaults to FolderOpenIcon. */
  Icon?: TreeIconComponent;
  /** Optional custom icon for the collapsed state. Defaults to `Icon` (if set) then FolderIcon. */
  IconCollapsed?: TreeIconComponent;
  /** When true, hides the drag-handle affordance. */
  disableDrag?: boolean;
  /**
   * False when the folder has nothing inside. The expand affordance is then
   * inert and dimmed: expanding onto an empty well reads as a broken toggle,
   * and the folder's own pane already lists what can be added.
   */
  hasChildren?: boolean;
}

export const RedoFolderItem = memo(function RedoFolderItem({
  name,
  friendlyName,
  dragHandleProps,
  onToggleExpanded,
  onRequestEdit,
  childrenLinks,
  childCount,
  expanded,
  clone,
  notificationCount,
  selected,
  onClick,
  onItemClick,
  Icon,
  IconCollapsed,
  disableDrag,
  hasChildren = true,
  "data-dd-action-name": actionName,
}: RedoFolderItemProps) {
  const [componentRef, setComponentRef] = useState<HTMLDivElement | null>(null);
  const [overflowRef, setOverflowRef] = useState<HTMLDivElement | null>(null);

  const location = useLocation();
  const matchedPath = childrenLinks.find((path) =>
    matchPath({ path }, location.pathname),
  );

  const componentHovered = useHover(componentRef);

  useEffect(() => {
    if (matchedPath && !expanded && hasChildren) {
      onToggleExpanded();
    }
    // I only want to run this once, on render
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const ExpandedIcon = Icon ?? FolderOpenIcon;
  const CollapsedIcon = IconCollapsed ?? Icon ?? FolderIcon;
  const bodyFiresSelect = onClick !== undefined;
  const toggleExpanded = hasChildren ? onToggleExpanded : undefined;

  return (
    <Flex
      as="button"
      borderRadius="md"
      className={classNames(
        navigationCss.navItem,
        folderItemCss.folderItem,
        selected && navigationCss.active,
      )}
      data-arbiter="RedoFolderItem"
      data-dd-action-name={actionName}
      data-testid={secondaryNavTabTestId(name)}
      justify="space-between"
      onClick={(e) => {
        e.preventDefault();
        onItemClick?.();
        if (bodyFiresSelect) {
          onClick();
          // Collapse only when this folder is already the selected one.
          // Clicking in from elsewhere should reveal the children you were
          // aiming for, never hide them.
          if (!expanded || selected) {
            toggleExpanded?.();
          }
        } else {
          toggleExpanded?.();
        }
      }}
      onContextMenu={(e) => {
        if (!onRequestEdit) {
          return;
        }
        e.preventDefault();
        e.stopPropagation();
        onRequestEdit();
      }}
      onKeyDown={(e) => {
        // WAI-ARIA tree pattern: arrows expand/collapse the focused folder,
        // so keyboard users aren't limited to the (nested) chevron button.
        if (e.key === "ArrowRight" && !expanded) {
          e.preventDefault();
          toggleExpanded?.();
        } else if (e.key === "ArrowLeft" && expanded) {
          e.preventDefault();
          toggleExpanded?.();
        }
      }}
      px="sm"
      py="sm"
      ref={setComponentRef}
      w="full"
      {...dragHandleProps}
    >
      <Flex
        align="center"
        basis={0}
        gap="md"
        grow={1}
        overflow="hidden"
        shrink={1}
        style={{ minWidth: 0 }}
      >
        <Flex shrink={0}>
          <RedoIcon
            color="tertiary"
            Icon={expanded ? ExpandedIcon : CollapsedIcon}
            size={16}
          />
        </Flex>
        <OverflowTooltip
          hideOnHover
          overflowRef={clone ? null : overflowRef}
          tooltipProps={{ title: friendlyName, darkMode: true }}
          wrapperStyle={{
            overflow: "hidden",
            display: "flex",
            alignItems: "center",
          }}
        >
          <Text
            fontSize="xs"
            overflow="hidden"
            ref={setOverflowRef}
            textColor="secondary"
            textOverflow="ellipsis"
            whiteSpace="nowrap"
          >
            {friendlyName}
          </Text>
        </OverflowTooltip>
      </Flex>
      {!disableDrag && (
        <Flex
          align="center"
          className={classNames(
            componentHovered && navigationCss.show,
            navigationCss.dragHandle,
            clone && navigationCss.clone,
          )}
          onClick={(e) => {
            e.preventDefault();
          }}
        >
          <RedoIcon color="tertiary" Icon={DotsReorderIcon} size={16} />
        </Flex>
      )}
      <Flex align="center" gap="xs">
        {notificationCount !== undefined && !expanded && (
          <RedoBadge
            className={navigationCss.notificationCountBadge}
            color={notificationCount > 0 ? "error" : "inactive"}
            darkMode
            size="xs"
            text={formatNotificationCount(notificationCount)}
            type="primary"
          />
        )}
        {bodyFiresSelect && hasChildren ? (
          <Flex
            align="center"
            aria-hidden
            as="button"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              toggleExpanded?.();
            }}
            tabIndex={-1}
          >
            <RedoIcon
              className={classNames(
                navigationCss.dropdownIcon,
                expanded && navigationCss.active,
              )}
              color="tertiary"
              Icon={TriangleDown}
              size={16}
            />
          </Flex>
        ) : (
          <RedoIcon
            className={classNames(
              navigationCss.dropdownIcon,
              expanded && navigationCss.active,
              !hasChildren && folderItemCss.dropdownIconEmpty,
            )}
            color="tertiary"
            Icon={TriangleDown}
            size={16}
          />
        )}
      </Flex>
      <Flex
        className={classNames(
          folderItemCss.pipeContainer,
          expanded && folderItemCss.expanded,
        )}
        style={
          // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
          {
            "--children-count": childCount ?? childrenLinks.length,
          } as React.CSSProperties
        }
      />
    </Flex>
  );
});
